/*
 * Copyright 2022 Oriflame
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { stringifyEntityRef } from '@backstage/catalog-model';
import { EntityScoreExtended } from '../../api/types';
import {
  DomainTitles,
  DomainTree,
  domainKey,
  domainTitleOf,
  topLevelOf,
} from './useDomainTree';

// Re-exported from their home in `useDomainTree`, which this module now
// depends on: moving them there broke a value cycle, and every call site —
// the table's Domain subtitle above all — keeps importing them from here.
export { domainKey, domainTitleOf } from './useDomainTree';
export type { DomainTitles } from './useDomainTree';

/**
 * Which slice of the portfolio the board is showing (feature 0039).
 *
 * A discriminated union rather than a string like `"domain:commerce"`: a third
 * axis (owner, verdict, …) becomes a new variant instead of a parser change,
 * and an unknown `type` — an older or newer portal writing a scope this build
 * does not understand — degrades to "show everything" by construction rather
 * than by remembering to handle it.
 */
export type BoardScope =
  | { type: 'all' }
  | { type: 'starred' }
  | { type: 'domain'; domain: string };

/**
 * The scope every failure path lands on. A personalisation that cannot be read
 * must never cost someone the board (ADR-0023).
 */
export const ALL_SCOPE: BoardScope = { type: 'all' };

/** Case-insensitive entity ref, so a stored ref and a score's ref compare. */
const refKey = (score: EntityScoreExtended): string =>
  stringifyEntityRef(score.entityRef).toLocaleLowerCase('en-US');

/**
 * Coerces anything read out of storage — or off a URL, or out of an older
 * build — into a scope this build can act on. Everything unrecognised is
 * `all`: a malformed preference is a reason to show the full board, never a
 * reason to show an error.
 */
export function normalizeScope(value: unknown): BoardScope {
  if (!value || typeof value !== 'object') {
    return ALL_SCOPE;
  }
  const candidate = value as { type?: unknown; domain?: unknown };
  if (candidate.type === 'starred') {
    return { type: 'starred' };
  }
  if (
    candidate.type === 'domain' &&
    typeof candidate.domain === 'string' &&
    candidate.domain.trim() !== ''
  ) {
    // Stored trimmed, so a padded value can never be saved and then fail to
    // match the catalog value it came from.
    return { type: 'domain', domain: candidate.domain.trim() };
  }
  return ALL_SCOPE;
}

/**
 * A row's Domain **as the board scopes on it**: its top-level ancestor, so a
 * System declaring a subdomain answers to the Domain that subdomain belongs to
 * (feature 0058). Without a tree — still loading, or no catalog API — it is
 * the row's own `spec.domain`, which is what the board did before subdomains.
 */
export function topLevelDomainOf(
  row: { domain?: string },
  tree: DomainTree | undefined,
): string | undefined {
  return topLevelOf(row.domain, tree);
}

/**
 * The **top-level** Domains present in the data, sorted, de-duplicated
 * case-insensitively. Empty until the first System's `catalog-info.yaml` gains
 * a `spec.domain` — which is what keeps the Domain control from rendering as a
 * dead option in the meantime.
 *
 * A subdomain is deliberately NOT an option (feature 0058): it is a row in the
 * table, and offering it here too would answer "whose board is this?" twice.
 * Its Systems are reached through its parent instead — the alternative, a
 * Retail Platform option that hides the three BitSvit Systems, is worse than
 * not grouping at all.
 */
export function availableDomains(
  scores: EntityScoreExtended[],
  tree?: DomainTree,
): string[] {
  const byKey = new Map<string, string>();
  for (const score of scores) {
    const raw = score.domain?.trim();
    if (!raw) continue;
    const top = topLevelOf(raw, tree);
    if (!top) continue;
    if (!byKey.has(top)) {
      // A System already on a top-level Domain keeps its own spelling, as it
      // always has; one rolled up to an ancestor is named by the canonical
      // key, which `domainTitleOf` turns into the Domain's title.
      byKey.set(top, top === domainKey(raw) ? raw : top);
    }
  }
  return [...byKey.values()].sort((a, b) =>
    a.localeCompare(b, 'en-US', { sensitivity: 'base' }),
  );
}

/**
 * Downgrades a scope the current data cannot honour to `all`, and lifts a
 * subdomain scope to the Domain that now carries it.
 *
 * The case that matters: a saved `domain` scope whose Domain has disappeared —
 * renamed, or the last System carrying it lost its `spec.domain`. Filtering on
 * it would leave an empty board under a control still claiming to show that
 * Domain. An empty **starred** scope is deliberately NOT downgraded: nothing
 * starred is the honest first-run state, and the board answers it with an
 * invitation to star something rather than by silently showing all 33.
 *
 * Feature 0058 adds the second job. A scope saved by an earlier build can name
 * a subdomain (`{type:'domain',domain:'bitsvit'}`), which the control no longer
 * offers. Rewriting it to the top-level ancestor keeps that reader on the
 * nearest thing to the board they left; falling through to `all` would quietly
 * widen it to the whole portfolio.
 */
export function resolveScope(
  scope: BoardScope,
  scores: EntityScoreExtended[],
  tree?: DomainTree,
): BoardScope {
  if (scope.type !== 'domain') {
    return scope;
  }
  const wanted = topLevelOf(scope.domain, tree);
  if (!wanted) {
    return ALL_SCOPE;
  }
  const exists = scores.some(score => topLevelDomainOf(score, tree) === wanted);
  if (!exists) {
    return ALL_SCOPE;
  }
  return wanted === domainKey(scope.domain)
    ? scope
    : { type: 'domain', domain: wanted };
}

/**
 * Narrows the portfolio to the scope's slice.
 *
 * `starredRefs` are stringified entity refs as `starredEntitiesApi` stores
 * them. A ref that matches no score — a System renamed or removed from the
 * catalog since it was starred — simply selects nothing: it is never a broken
 * row and never an error (compare feature 0023).
 */
export function applyScope<T extends EntityScoreExtended>(
  scores: T[],
  scope: BoardScope,
  starredRefs: Iterable<string> = [],
  tree?: DomainTree,
): T[] {
  switch (scope.type) {
    case 'starred': {
      const starred = new Set(
        [...starredRefs].map(ref => ref.toLocaleLowerCase('en-US')),
      );
      return scores.filter(score => starred.has(refKey(score)));
    }
    case 'domain': {
      // Compared on the TOP-LEVEL ancestor (feature 0058), so narrowing to
      // Retail Platform keeps the Systems that declare the BitSvit subdomain.
      const wanted = topLevelOf(scope.domain, tree);
      return scores.filter(score => topLevelDomainOf(score, tree) === wanted);
    }
    default:
      return scores;
  }
}

/**
 * What {@link matchesSearch} can be handed: a System's score, or a group row
 * with members under it. Typed structurally rather than importing `BoardRow`,
 * which would point this module back at one that depends on it.
 */
type SearchableRow = EntityScoreExtended & { children?: SearchableRow[] };

/**
 * Free-text match over what a person can actually see on a row: the catalog
 * Title, the entity name, and the Domain subtitle (its catalog title, or the
 * raw `spec.domain` until titles load — feature 0057). This is where the
 * board's search lives now — material-table matched a column's `field`, and
 * since feature 0013 the headline column's field has been the numeric
 * `scorePercent`, so its box could only ever match a score.
 */
export function matchesSearch(
  score: SearchableRow,
  text: string | undefined,
  domainTitles?: DomainTitles,
): boolean {
  const needle = text?.trim().toLocaleLowerCase('en-US');
  if (!needle) {
    return true;
  }
  const haystack = [
    score.title,
    score.entityRef?.name,
    domainTitleOf(score.domain, domainTitles),
  ];
  if (
    haystack.some(value => value?.toLocaleLowerCase('en-US').includes(needle))
  ) {
    return true;
  }
  // A hit inside a group keeps the group (feature 0058): the row a reader is
  // looking for is in there, and the table expands it. Dissolving the
  // hierarchy while someone types would teach them nothing about it.
  return (score.children ?? []).some(child =>
    matchesSearch(child, text, domainTitles),
  );
}
